import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import posthog from 'posthog-js';
import { usePromptQueue, PromptPriority } from '@/contexts/PromptQueueContext';
import { useFeedbackTracker } from '@/components/FeedbackPromptProvider';
import {
  FEEDBACK_SHOWN_SESSION_KEY,
  REVIEW_PROMPTED_SESSION_KEY,
  shouldShowReviewPrompt,
  useAppReviewPrompt,
} from '@/hooks/useAppReviewPrompt';
import { shouldShowFeedbackPrompt } from '@/hooks/useFeedbackPrompt';
import { openExternal } from '@/lib/externalNavigation';
import { AppReviewPromptDialog } from '@/components/AppReviewPromptDialog';
import { FeedbackDialog } from '@/components/FeedbackDialog';

const REVIEW_URL = 'https://apps.apple.com/app/id6759308140?action=write-review';
const PROMPT_ID = 'app-review';

interface ReviewWinMomentContextType {
  notifyWinMoment: () => void;
}

const noop = () => {};

const ReviewWinMomentContext = createContext<ReviewWinMomentContextType | null>(null);

export function useReviewWinMoment() {
  const ctx = useContext(ReviewWinMomentContext);
  if (!ctx) {
    // No-op fallback when used outside provider (mirrors useFeedbackTracker).
    return { notifyWinMoment: noop };
  }
  return ctx;
}

interface AppReviewPromptProviderProps {
  children: ReactNode;
}

export function AppReviewPromptProvider({ children }: AppReviewPromptProviderProps) {
  const { subscribeWinMoment } = useFeedbackTracker();
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();
  const { engagement, markShown, markRated, markDeclined } = useAppReviewPrompt();

  const [feedbackFallbackOpen, setFeedbackFallbackOpen] = useState(false);

  // Stable ref to always-latest eligibility state so notifyWinMoment (which is
  // subscribed once) reads current values without needing to re-subscribe.
  const stateRef = useRef({ engagement, isActivePrompt });
  stateRef.current = { engagement, isActivePrompt };

  const active = isActivePrompt(PROMPT_ID);

  // Fire "shown" analytics + persist the prompt-count once, when the dialog
  // actually becomes active.
  const shownFiredRef = useRef(false);
  useEffect(() => {
    if (active && !shownFiredRef.current) {
      shownFiredRef.current = true;
      try {
        posthog.capture('review_prompt_shown');
      } catch { /* ignore */ }
      void markShown();
    }
    if (!active) {
      shownFiredRef.current = false;
    }
  }, [active, markShown]);

  const notifyWinMoment = useCallback(() => {
    if (typeof sessionStorage === 'undefined') return;

    // 1. Per-session guard — at most one review prompt per browser session.
    if (sessionStorage.getItem(REVIEW_PROMPTED_SESSION_KEY)) return;

    // 2. Anti-stacking with feedback prompt (skip, do not queue).
    if (sessionStorage.getItem(FEEDBACK_SHOWN_SESSION_KEY)) return;
    const { engagement: eng, isActivePrompt: isActive } = stateRef.current;
    if (isActive('feedback')) return;
    // Passing Number.POSITIVE_INFINITY treats the feedback prompt's check-in
    // threshold as satisfied here, so if feedback would otherwise be eligible
    // we conservatively skip the review prompt and let feedback win the tie.
    if (shouldShowFeedbackPrompt(eng, Number.POSITIVE_INFINITY)) return;

    // 3. Review-side eligibility (isNativeApp, session count, cap, cooldown).
    if (!shouldShowReviewPrompt(eng)) return;

    // Passed all gates — set the session flag *before* requesting so a
    // synchronous second call in the same tick still short-circuits.
    sessionStorage.setItem(REVIEW_PROMPTED_SESSION_KEY, 'true');
    requestPrompt(PROMPT_ID, PromptPriority.APP_REVIEW);
  }, [requestPrompt]);

  // Subscribe once to the feedback provider's trackAction fan-out.
  useEffect(() => {
    const unsubscribe = subscribeWinMoment(notifyWinMoment);
    return unsubscribe;
  }, [subscribeWinMoment, notifyWinMoment]);

  const handleAccept = useCallback(() => {
    try {
      posthog.capture('review_prompt_accepted', { method: 'store_url' });
    } catch { /* ignore */ }
    openExternal(REVIEW_URL);
    void markRated();
    releasePrompt(PROMPT_ID);
  }, [markRated, releasePrompt]);

  const handleDecline = useCallback(() => {
    try {
      posthog.capture('review_prompt_declined');
    } catch { /* ignore */ }
    void markDeclined();
    releasePrompt(PROMPT_ID);
    setFeedbackFallbackOpen(true);
  }, [markDeclined, releasePrompt]);

  const handleDismiss = useCallback(() => {
    // Only fire dismiss analytics when the dialog was actually open and this
    // isn't the close triggered by handleAccept/handleDecline (both of which
    // release the slot before onOpenChange(false) runs, so `active` is already
    // false by the time this callback fires from those paths).
    if (active) {
      try {
        posthog.capture('review_prompt_dismissed');
      } catch { /* ignore */ }
      // markShown already stamped last_review_prompted_at and incremented the
      // count when the dialog opened — no extra write needed on X close.
      releasePrompt(PROMPT_ID);
    }
  }, [active, releasePrompt]);

  return (
    <ReviewWinMomentContext.Provider value={{ notifyWinMoment }}>
      {children}
      <AppReviewPromptDialog
        open={active}
        onDismiss={handleDismiss}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />
      <FeedbackDialog
        open={feedbackFallbackOpen}
        onOpenChange={setFeedbackFallbackOpen}
        onSubmitSuccess={() => setFeedbackFallbackOpen(false)}
      />
    </ReviewWinMomentContext.Provider>
  );
}
